import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle as _DialogTitle,
  Typography,
} from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCompleteAssignment } from '../../../../hooks/offer/useCompleteAssignment';
import theme from '../../../../theme';
import Spinner from '../../../component/compound/Spinner';
import LfToBr from '../../../component/primitive/LfToBr';
import { SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';

interface Props extends ClassAttributes<HTMLElement> {
  isOpen: boolean;
  assignmentId: string;
  reload: () => Promise<unknown>;
  closeDialog: () => void;
}

const CompleteDialog = ({ isOpen, assignmentId, reload, closeDialog }: ClassAttributes<HTMLElement> & Props) => {
  const { completeAssignment, loadingAssignment } = useCompleteAssignment();

  return (
    <Dialog open={isOpen} onClose={closeDialog} disableBackdropClick={true}>
      <DialogTitle disableTypography>
        <Typography variant="h6">
          <FormattedMessage id="reputation_dialog.input_reputation" />
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loadingAssignment ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : null}

        <Lead variant="body2">
          <FormattedMessage id="reputation_dialog.please_review">
            {(text: string) => <LfToBr text={text} />}
          </FormattedMessage>
        </Lead>
      </DialogContent>

      <DialogActions>
        <Button
          variant="text"
          disabled={loadingAssignment}
          onClick={async () => {
            await completeAssignment(assignmentId);
            reload();
            closeDialog();
          }}
        >
          <FormattedMessage id="reputation_dialog.submit" />
        </Button>
        <Button variant="text" color="default" disabled={loadingAssignment} onClick={closeDialog}>
          <FormattedMessage id="reputation_dialog.cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogTitle = styled(_DialogTitle)({
  borderBottom: `1px solid ${theme.palette.grey['200']}`,
});

const DialogContent = styled(_DialogContent)(
  gridLayoutContainer({
    columns: ['100%'],
    rows: ['auto'],
    areas: [['lead']],
  }),
  {
    position: 'relative',
    padding: SPACING_UNIT * 3,
  },
);

const Lead = styled(Typography)(gridLayoutItem('lead'));

const DialogActions = styled(_DialogActions)({
  borderTop: `1px solid ${theme.palette.grey['200']}`,
  marginTop: 0,
  paddingTop: SPACING_UNIT * 2,
  paddingBottom: SPACING_UNIT,
  height: 60,
  flexDirection: 'row-reverse',
  justifyContent: 'end',
  [mq('sp')]: {
    height: 'auto',
    display: 'block',
    '> button': {
      display: 'block',
      marginTop: SPACING_UNIT,
      width: `calc( 100% - ${SPACING_UNIT}px * 2 )`,
    },
  },
});

const SpinnerContainer = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }), {
  position: 'absolute',
  width: '100%',
  top: 0,
  bottom: 0,
  background: 'rgba(255, 255, 255, 0.8)',
  zIndex: 100,
});

export default CompleteDialog;
